import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { isLogged } from '@/utils/auth/useAuthStatusMutation';
import { useLatestLoanQuery } from '@/hooks/webapi/loans/useLatestLoanQuery';
import { useClient } from '@/hooks/webapi';
import { getDashboardRedirectionUrl } from '@/utils/redirectionUrl';
import style from './WaitingPage.module.scss';
import LoadingAnimation from '@/assets/lottie/loading-vertical-lines.json';
import { useClientApplicationCheckStatusQuery } from '@/hooks/webapi/client/useClientApplicationCheckStatusQuery';
import useNavigateWithBudgetResolution from '@/hooks/resolutionResolvers/useNavigateWithBudgetResolution';
import useNavigateWithLoanConfirmResolution from '@/hooks/resolutionResolvers/useNavigateWithLoanConfirmResolution';
import useNavigateWithUpsaleResolution from '@/hooks/resolutionResolvers/useNavigateWithUpsaleResolution';
import useNavigateWithAdditionalAmountBudgetResolution from '@/hooks/resolutionResolvers/useNavigateWithAdditionalAmountBudgetResolution';
import useNavigateWithTopUpResolution from '@/hooks/resolutionResolvers/useNavigateWithTopUpResolution';
import { useResolutionResolverAuthorization } from '@/hooks/resolutionResolvers/useResolutionResolverAuthorization';
import getClientApplication, { Application } from '@/api/getClientApplication';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import IdentifyEvent from '@/hooks/context/EventCollector/events/IdentifyEvent';

const LottieOptions = {
  loop: true,
  autoplay: true,
  animationData: LoadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export default function WaitingPage() {
  const location = useLocation();
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const { navigateWithBudgetResolution } = useNavigateWithBudgetResolution();
  const { navigateWithAdditionalAmountBudgetResolution } =
    useNavigateWithAdditionalAmountBudgetResolution();
  const { navigateWithUpsaleResolution } = useNavigateWithUpsaleResolution();
  const { navigateWithLoanConfirmResolution } =
    useNavigateWithLoanConfirmResolution();
  const { navigateWithTopUpResolution } = useNavigateWithTopUpResolution();
  const { resolveAuthorization } = useResolutionResolverAuthorization();
  const { identify } = useEventCollector();
  const navigate = useNavigate();
  const { client } = useClient();
  const { refetch: fetchLatestLoan } = useLatestLoanQuery(isLogged());
  const { t } = useTranslation('waiting_page');

  const { data: checkStatusData } =
    useClientApplicationCheckStatusQuery(isCheckingStatus);

  const queryClient = useQueryClient();

  const handleNavigation = useCallback(
    (application: Application) => {
      const { state } = location;

      if (
        typeof state === 'object' &&
        state !== null &&
        'from' in state &&
        typeof state.from === 'string'
      ) {
        const { from } = state;

        switch (from) {
          case 'Login':
          case 'TwoFA':
          case 'OneTimeTokenLogin':
          case 'NewPassword':
          case 'Keycloak':
            resolveAuthorization(application);
            return;
          case 'LoanBudget':
          case 'AdjustIncome':
          case 'verified':
            navigateWithBudgetResolution(application);
            return;
          case 'AdditionalAmountBudget':
            navigateWithAdditionalAmountBudgetResolution(application);
            return;
          case 'NewOfferIncreased':
            navigateWithUpsaleResolution(application);
            return;
          case 'TopUpAmount':
            navigateWithTopUpResolution(application);
            return;
          case 'LoanConfirm':
            if (
              'isNotRepeatedLoan' in state &&
              typeof state.isNotRepeatedLoan === 'boolean'
            ) {
              navigateWithLoanConfirmResolution(
                application,
                state.isNotRepeatedLoan,
              );
            }
            break;
          default:
        }
      }
    },
    [location, navigateWithBudgetResolution, navigateWithLoanConfirmResolution],
  );

  // case when the risks arent run (and check status will always return false).
  // On initial render it will navigate per application state
  useEffect(() => {
    getClientApplication()
      .then((application) => {
        if (application?.riskEvaluationStarted === false) {
          handleNavigation(application);
          identify(new IdentifyEvent({ application }));
          queryClient.removeQueries({
            queryKey: ['client', 'application', 'check-status'],
          });
        }
      })
      .catch((error) => {
        if (error.response?.status === 500) {
          navigate(getDashboardRedirectionUrl(client?.hasOpenLoan));
        }
      });
  }, []);

  // case when we need to check status of underwriting
  useEffect(() => {
    if (checkStatusData?.completed) {
      setIsCheckingStatus(false);
      getClientApplication().then((application) => {
        handleNavigation(application);
        identify(new IdentifyEvent({ application }));
        queryClient.removeQueries({
          queryKey: ['client', 'application', 'check-status'],
        });
        fetchLatestLoan(); // Get latest loan updated for next screen
      });
    }
  }, [checkStatusData?.completed]);

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>
      <div className={style.lottie} data-test="waitingPage-lottie-wrapper">
        <Lottie width={200} options={LottieOptions} />
      </div>
      <div className={style.title} data-test="waitingPage-title">
        {t('title')}
      </div>
      <div className={style.info} data-test="waitingPage-info">
        {t('info')}
      </div>
    </>
  );
}
